import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { connect } from "react-redux";
import InternalEstimateCreate from './Create';
import InternalEstimateDetail from './Detail';
import InternalEstimateList from './List';
import Common from '../../../../../shared/common';
import { allMessages } from "../../../../../../_metronic/i18n/I18nProvider";

// Priority order for determining contributing estimate:
// 1. PM Assessment / Variation Order (higher priority)
// 2. Accepted Compensation Event Quotation
// 3. Highest value CE Quotation awaiting PM Response
// 4. Not accepted CE Quotation
// 5. Claim value
// 6. Internal Estimate for Instruction linked to CE
// 7. Internal Estimate for Early Warning linked to CE (lowest priority)

export const internalEstimateLifecycleStages = ['EW', 'INSTRUCTION', 'CE'];
export const internalEstimateStatuses = ['ACTIVE', 'SUPERSEDED', 'MANUALLYSUPERSEDED'];

const defaultFields = {
    PriceImpact: { visible: false, label: '', required: false },
    TimeImpact: { visible: false, label: '', required: false },
    InternalNotes: { visible: false, label: '', required: false }
};

export const getFields = (resources, placeholdersQA, workflowsQA, languageFile, wfid, globalResources, contract, componentProps) => {
    const fields = { ...defaultFields };

    if (resources.InternalEstimatePriceLabel) {
        fields.PriceImpact = { visible: true, label: resources.InternalEstimatePriceLabel, required: true };
    } else {
        fields.PriceImpact = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.PRICEIMPACT"], required: true };
    }

    if (resources.InternalEstimateTimeLabel) {
        fields.TimeImpact = { visible: true, label: resources.InternalEstimateTimeLabel, required: true };
    } else {
        fields.TimeImpact = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.TIMEIMPACT"], required: true };
    }

    fields.InternalNotes = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.INTERNALNOTES"] || 'Internal Notes', required: true };
    
    return fields;
};

/**
 * Generic Internal Estimates Router
 * Can be reused across all workflows (Compensation Events, Early Warnings, Instructions, Proposals)
 * 
 * Props required:
 * - parentWorkflowItem: The parent workflow configuration
 * - parentNotification: The parent notification/workflow object (CE, EW, Instruction, Proposal)
 * - parentTabPanel: Reference to parent tab panel
 * - onParentChanged: Callback when estimate changes
 * - workflowType: 'CE', 'EW', 'INSTRUCTION', 'PROPOSAL' (determines path construction)
 */
class InternalEstimateRouter extends React.Component {
    constructor(props) {
        super(props);
        
        this.notificationId = props.notificationid;
        this.referenceType = 'InternalEstimate';

        this.notificationName = 'Internal Estimate';
        this.pageTitle = this.props.intl.formatMessage({ id: 'GENERAL.INTERNALESTIMATE.TITLE' });

        const fields = getFields(props.resources, props.placeholdersQA, props.workflowsQA, allMessages[this.props.lang], this.props.workflowItem.id, props.globalResources, props.contract, props);
        
        // Create custom WorkflowItem for Internal Estimate
        this.workflowItem = {
            ...props.parentWorkflowItem,
            fields: fields,
            pageTitle: this.pageTitle,
            notifyingParty: this.notifyingParty,
            userFilters: {
                ...props.parentWorkflowItem.userFilters,
                mainNotice: {
                    // createFilter: props.parentWorkflowItem.userFilters.estimateMainNotice?.createEstimateFilter,
                    // createAllowed: props.parentWorkflowItem.userFilters.estimateMainNotice?.createEstimateAllowed,
                    createFilter: props.parentWorkflowItem.userFilters.mainNotice?.createFilter,
                    createAllowed: props.parentWorkflowItem.userFilters.mainNotice?.createAllowed,
                    // communicateFilter: props.parentWorkflowItem.userFilters.estimateMainNotice?.communicateEstimateFilter,
                    // communicateAllowed: props.parentWorkflowItem.userFilters.estimateMainNotice?.communicateEstimateAllowed
                }
            },
            // Generic paths that work for any workflow type
            paths: {
                ...props.parentWorkflowItem.paths,
                basePath: props.parentWorkflowItem.paths.basePath + this.notificationId + "/estimate/",
                createPath: props.parentWorkflowItem.paths.basePath + this.notificationId + "/estimate/create",
                listPath: props.parentWorkflowItem.paths.basePath + this.notificationId,
                defaultPath: props.parentWorkflowItem.paths.basePath + this.notificationId,
            },
            config: {
                ...props.parentWorkflowItem.config
            }
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.parentNotification !== nextProps.parentNotification) {
            return true;
        }
        return false;
    }

    render() {
        return (
            <Routes caseSensitive>
                <Route
                    path={'/estimate/create'}
                    exact
                    element={Common.createAllowedInternalEstimate(this.workflowItem) ? (
                            <InternalEstimateCreate
                                {...this.props}
                                parentTabPanel={this.props.parentTabPanel}
                                title={this.notificationName}
                                workflowItem={this.workflowItem}
                                parentNotification={this.props.parentNotification}
                            />) : (
                            <Navigate replace to={this.workflowItem.paths.defaultPath} />
                        )
                    }
                />

                <Route
                    path={'/estimate/:estimateid'}
                    exact
                    element={<InternalEstimateDetail
                        {...this.props}
                        parentTabPanel={this.props.parentTabPanel}
                        onParentChanged={this.props.onParentChanged}
                        title={this.notificationName}
                        notifyUserFilter={this.props.notifyUserFilter}
                        workflowItem={this.workflowItem}
                        parentNotification={this.props.parentNotification}
                    />}
                />

                <Route
                    // path={'/estimate'}
                    // exact
                    path="*"
                    element={<InternalEstimateList
                        {...this.props}
                        title={this.notificationName}
                        workflowItem={this.workflowItem}
                        parentNotification={this.props.parentNotification}
                    />}
                />

                <Route path="*" element={<Navigate replace to={this.workflowItem.paths.defaultPath} />} />
            </Routes>
        );
    }
}

const mapStateToProps = store => ({
    lang: store.i18n.lang,
});
export default connect(mapStateToProps)(InternalEstimateRouter);